import { useState } from 'react';

const legs = [
  { name: 'Crosswind', alt: '1000 ft', speed: '90 kts', score: 96, color: '#00FFD1', altVal: 1000, spdVal: 90 },
  { name: 'Downwind', alt: '1000 ft', speed: '90 kts', score: 94, color: '#00FFD1', altVal: 1000, spdVal: 90 },
  { name: 'Base', alt: '700 ft', speed: '80 kts', score: 88, color: '#B4FF39', altVal: 700, spdVal: 80 },
  { name: 'Final', alt: '300 ft', speed: '70 kts', score: 92, color: '#00FFD1', altVal: 300, spdVal: 70 },
  { name: 'Flare', alt: '0 ft', speed: '60 kts', score: 95, color: '#00FFD1', altVal: 0, spdVal: 60 },
];

// Glidepath profile data points (distance from threshold in NM vs altitude)
const glidepathIdeal = [
  { d: 5.0, alt: 1500 }, { d: 4.0, alt: 1200 }, { d: 3.0, alt: 900 },
  { d: 2.0, alt: 600 }, { d: 1.0, alt: 300 }, { d: 0.0, alt: 0 },
];
const glidepathFlown = [
  { d: 5.0, alt: 1480 }, { d: 4.0, alt: 1150 }, { d: 3.0, alt: 870 },
  { d: 2.0, alt: 560 }, { d: 1.0, alt: 280 }, { d: 0.0, alt: 0 },
];

const airspeedProfile = [
  { label: 'Crosswind', ideal: 90, flown: 92 },
  { label: 'Downwind', ideal: 90, flown: 89 },
  { label: 'Base', ideal: 80, flown: 83 },
  { label: 'Final', ideal: 70, flown: 72 },
  { label: 'Flare', ideal: 60, flown: 61 },
];

export default function FeaturePatternAnalysis() {
  const [activeLeg, setActiveLeg] = useState<number | null>(null);

  const gpW = 500, gpH = 120;
  const gpMaxD = 5.0, gpMaxAlt = 1600;

  const toGpXY = (d: number, alt: number) => ({
    x: ((gpMaxD - d) / gpMaxD) * (gpW - 20) + 10,
    y: gpH - (alt / gpMaxAlt) * (gpH - 16) - 8,
  });

  const idealPts = glidepathIdeal.map((p) => toGpXY(p.d, p.alt));
  const flownPts = glidepathFlown.map((p) => toGpXY(p.d, p.alt));

  return (
    <section id="pattern-analysis" className="py-16 sm:py-24 bg-[#08121F]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 mb-12 sm:mb-16">
          <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-[#B4FF39]/10 border border-[#B4FF39]/30 rounded-2xl flex-shrink-0">
            <i className="ri-flight-takeoff-line text-2xl sm:text-3xl text-[#B4FF39]"></i>
          </div>
          <div>
            <div className="text-xs text-[#B4FF39] uppercase tracking-widest font-semibold mb-2">Capability 03</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4">Pattern &amp; Landing Analysis</h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-300 max-w-3xl leading-relaxed">
              WingmanFix breaks down every traffic pattern leg and landing into precise data points — altitude, airspeed, geometry, and timing — giving you a complete picture of each approach.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 sm:gap-10 items-start">
          {/* Left: Pattern Diagram + Glidepath */}
          <div className="flex flex-col gap-4 sm:gap-6">
            <div className="bg-[#0D1B2E] rounded-2xl sm:rounded-3xl p-4 sm:p-8 border border-[#B4FF39]/20 shadow-2xl">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="text-xs sm:text-base font-semibold text-white uppercase tracking-wider">Pattern Replay — RWY 28L</h3>
                <span className="text-xs text-[#B4FF39] bg-[#B4FF39]/10 border border-[#B4FF39]/30 px-2 sm:px-3 py-1 rounded-full whitespace-nowrap">Apr 11, 2025</span>
              </div>

              {/* Top-down pattern diagram */}
              <div className="relative bg-[#050A14] rounded-xl sm:rounded-2xl overflow-hidden mb-4 sm:mb-6 w-full" style={{ height: 240 }}>
                <svg className="w-full h-full" viewBox="0 0 620 300" preserveAspectRatio="xMidYMid meet">
                  {/* Grid */}
                  {[1,2,3,4,5].map((i) => (
                    <line key={`h${i}`} x1="0" y1={i*50} x2="620" y2={i*50} stroke="#0D1B2E" strokeWidth="1" />
                  ))}
                  {[1,2,3,4,5,6,7,8,9,10,11].map((i) => (
                    <line key={`v${i}`} x1={i*56} y1="0" x2={i*56} y2="300" stroke="#0D1B2E" strokeWidth="1" />
                  ))}

                  {/* Runway */}
                  <rect x="260" y="230" width="90" height="18" rx="3" fill="#374151" />
                  <rect x="300" y="230" width="10" height="18" fill="#F6E05E" opacity="0.5" />
                  <rect x="320" y="230" width="10" height="18" fill="#F6E05E" opacity="0.5" />
                  <text x="305" y="262" textAnchor="middle" fill="#6B7280" fontSize="10" fontFamily="monospace">RWY 28L</text>

                  {/* Touchdown zone markers */}
                  <rect x="268" y="234" width="6" height="10" rx="1" fill="#F6E05E" opacity="0.7" />
                  <rect x="336" y="234" width="6" height="10" rx="1" fill="#F6E05E" opacity="0.7" />

                  {/* Wind indicator */}
                  <g transform="translate(560, 40)">
                    <circle cx="0" cy="0" r="18" fill="#0A1628" stroke="#374151" strokeWidth="1" />
                    <line x1="0" y1="-12" x2="0" y2="12" stroke="#9CA3AF" strokeWidth="1" />
                    <line x1="-12" y1="0" x2="12" y2="0" stroke="#9CA3AF" strokeWidth="1" />
                    <polygon points="0,-14 -4,-6 4,-6" fill="#00FFD1" />
                    <text x="0" y="28" textAnchor="middle" fill="#6B7280" fontSize="8" fontFamily="monospace">270/8</text>
                  </g>

                  {/* Ideal pattern path */}
                  <polyline
                    points="305,230 305,70 520,70 520,230 400,230"
                    fill="none" stroke="#B4FF39" strokeWidth="2" strokeDasharray="8 4" opacity="0.5"
                  />

                  {/* Flown path */}
                  <polyline
                    points="305,230 307,72 518,74 516,232 398,232"
                    fill="none" stroke="#00FFD1" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
                  />

                  {/* Leg highlight on hover */}
                  {activeLeg === 0 && <line x1="305" y1="230" x2="307" y2="72" stroke="#00FFD1" strokeWidth="5" opacity="0.5" />}
                  {activeLeg === 1 && <line x1="307" y1="72" x2="518" y2="74" stroke="#00FFD1" strokeWidth="5" opacity="0.5" />}
                  {activeLeg === 2 && <line x1="518" y1="74" x2="516" y2="232" stroke="#00FFD1" strokeWidth="5" opacity="0.5" />}
                  {activeLeg === 3 && <line x1="516" y1="232" x2="398" y2="232" stroke="#00FFD1" strokeWidth="5" opacity="0.5" />}

                  {/* Leg labels */}
                  <text x="200" y="155" fill="#6B7280" fontSize="10" fontFamily="monospace">CROSSWIND</text>
                  <text x="530" y="155" fill="#6B7280" fontSize="10" fontFamily="monospace">DOWNWIND</text>
                  <text x="440" y="255" fill="#6B7280" fontSize="10" fontFamily="monospace">BASE</text>
                  <text x="320" y="215" fill="#6B7280" fontSize="10" fontFamily="monospace">FINAL</text>

                  {/* Aircraft position */}
                  <circle cx="398" cy="232" r="8" fill="#00FFD1" opacity="0.2" className="animate-ping" />
                  <circle cx="398" cy="232" r="5" fill="#00FFD1" />
                  <text x="398" y="220" textAnchor="middle" fill="#00FFD1" fontSize="12">✈</text>

                  {/* Deviation annotation */}
                  <line x1="516" y1="74" x2="520" y2="70" stroke="#F59E0B" strokeWidth="1.5" strokeDasharray="3 2" />
                  <circle cx="518" cy="72" r="4" fill="none" stroke="#F59E0B" strokeWidth="1.5" />
                  <text x="530" y="68" fill="#F59E0B" fontSize="9" fontFamily="monospace">-50ft</text>

                  {/* Legend */}
                  <line x1="20" y1="285" x2="50" y2="285" stroke="#B4FF39" strokeWidth="2" strokeDasharray="6 3" />
                  <text x="58" y="289" fill="#6B7280" fontSize="9">Ideal path</text>
                  <line x1="120" y1="285" x2="150" y2="285" stroke="#00FFD1" strokeWidth="2.5" />
                  <text x="158" y="289" fill="#6B7280" fontSize="9">Flown path</text>
                  <circle cx="230" cy="285" r="4" fill="#F59E0B" />
                  <text x="240" y="289" fill="#6B7280" fontSize="9">Deviation</text>
                </svg>
              </div>

              {/* Leg breakdown */}
              <div className="space-y-2">
                {legs.map((leg, i) => (
                  <button
                    key={i}
                    onMouseEnter={() => setActiveLeg(i)}
                    onMouseLeave={() => setActiveLeg(null)}
                    className={`w-full flex items-center gap-2 sm:gap-4 rounded-xl px-3 sm:px-5 py-2 sm:py-3 border transition-all cursor-pointer ${
                      activeLeg === i ? 'border-[#00FFD1]/50 bg-[#00FFD1]/5' : 'border-gray-800 bg-[#0A1628] hover:border-gray-600'
                    }`}
                  >
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider w-16 sm:w-20 flex-shrink-0 text-left truncate">{leg.name}</span>
                    <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                      <span className="text-xs text-gray-500 w-12 sm:w-16 text-left truncate">{leg.alt}</span>
                      <span className="text-xs text-gray-500 w-12 sm:w-16 text-left truncate">{leg.speed}</span>
                      <div className="flex-1 h-2 bg-[#1A2942] rounded-full overflow-hidden min-w-0">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${leg.score}%`, backgroundColor: leg.color, boxShadow: activeLeg === i ? `0 0 8px ${leg.color}80` : 'none' }}
                        ></div>
                      </div>
                    </div>
                    <span className="text-sm font-bold w-6 sm:w-8 text-right flex-shrink-0" style={{ color: leg.color }}>{leg.score}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Glidepath Profile */}
            <div className="bg-[#0D1B2E] rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-gray-700 shadow-xl overflow-x-auto">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <h3 className="text-xs sm:text-sm font-semibold text-white uppercase tracking-wider">Glidepath Profile</h3>
                <div className="flex items-center gap-2 sm:gap-4 text-xs text-gray-500">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 sm:w-4 h-px bg-[#B4FF39] border-dashed border-t border-[#B4FF39]"></div>
                    <span className="hidden sm:inline">Ideal 3°</span>
                    <span className="sm:hidden">Ideal</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 sm:w-4 h-0.5 bg-[#00FFD1]"></div>
                    <span>Flown</span>
                  </div>
                </div>
              </div>
              <div className="relative min-w-[500px]">
                <svg className="w-full" style={{ height: gpH + 28 }} viewBox={`0 0 ${gpW} ${gpH + 28}`} preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="gpGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#00FFD1" stopOpacity="0.15" />
                      <stop offset="100%" stopColor="#00FFD1" stopOpacity="0" />
                    </linearGradient>
                  </defs>

                  {/* Grid */}
                  {[400, 800, 1200].map((alt) => {
                    const y = gpH - (alt / gpMaxAlt) * (gpH - 16) - 8;
                    return (
                      <g key={alt}>
                        <line x1="0" y1={y} x2={gpW} y2={y} stroke="#1A2942" strokeWidth="1" />
                        <text x="4" y={y - 3} fill="#374151" fontSize="9" fontFamily="monospace">{alt}ft</text>
                      </g>
                    );
                  })}

                  {/* Ideal path */}
                  <polyline
                    points={idealPts.map((p) => `${p.x},${p.y}`).join(' ')}
                    fill="none" stroke="#B4FF39" strokeWidth="2" strokeDasharray="8 4" opacity="0.6"
                  />

                  {/* Flown area */}
                  <polygon
                    points={[
                      ...flownPts.map((p) => `${p.x},${p.y}`),
                      `${flownPts[flownPts.length - 1].x},${gpH}`,
                      `${flownPts[0].x},${gpH}`,
                    ].join(' ')}
                    fill="url(#gpGrad)"
                  />
                  <polyline
                    points={flownPts.map((p) => `${p.x},${p.y}`).join(' ')}
                    fill="none" stroke="#00FFD1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                  />

                  {/* Data points */}
                  {flownPts.map((p, i) => (
                    <circle key={i} cx={p.x} cy={p.y} r="4" fill="#00FFD1" stroke="#08121F" strokeWidth="1.5" />
                  ))}

                  {/* Runway threshold */}
                  <rect x={gpW - 14} y={gpH - 8} width="14" height="8" fill="#374151" />
                  <text x={gpW - 7} y={gpH + 16} textAnchor="middle" fill="#6B7280" fontSize="9" fontFamily="monospace">THR</text>

                  {/* X-axis labels */}
                  {glidepathIdeal.map((p, i) => {
                    const x = ((gpMaxD - p.d) / gpMaxD) * (gpW - 20) + 10;
                    return (
                      <text key={i} x={x} y={gpH + 16} textAnchor="middle" fill="#4B5563" fontSize="9" fontFamily="monospace">
                        {p.d > 0 ? `${p.d}NM` : ''}
                      </text>
                    );
                  })}
                </svg>
              </div>
            </div>
          </div>

          {/* Right: Details */}
          <div className="flex flex-col gap-4 sm:gap-6">
            {/* Landing score card */}
            <div className="relative rounded-xl sm:rounded-2xl overflow-hidden border border-[#B4FF39]/20 shadow-xl">
              <img
                src="https://public.readdy.ai/ai/img_res/1a7c42f3d5a7a16a2a58c9b3924d63e9.jpg"
                alt="Aircraft on final approach"
                className="w-full h-44 sm:h-52 object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#08121F] via-[#08121F]/40 to-transparent"></div>
              <div className="absolute bottom-3 sm:bottom-4 left-4 sm:left-5 right-4 sm:right-5 flex items-end justify-between">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wider">Landing Score</p>
                  <p className="text-3xl sm:text-4xl font-bold text-[#B4FF39]">93</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-400">Touchdown Zone</p>
                  <p className="text-xs sm:text-sm font-semibold text-white">+320 ft from threshold</p>
                </div>
              </div>
            </div>

            {/* Airspeed profile chart */}
            <div className="bg-[#0D1B2E] rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-700">
              <h3 className="text-xs sm:text-sm font-semibold text-white uppercase tracking-wider mb-3 sm:mb-4">Airspeed Profile vs. Target</h3>
              <div className="space-y-2 sm:space-y-3">
                {airspeedProfile.map((ap, i) => (
                  <div key={i} className="flex items-center gap-2 sm:gap-3">
                    <span className="text-xs text-gray-500 w-16 sm:w-20 flex-shrink-0 truncate">{ap.label}</span>
                    <div className="flex-1 relative h-5 sm:h-6 bg-[#0A1628] rounded-lg overflow-hidden min-w-0">
                      {/* Ideal bar */}
                      <div
                        className="absolute top-1 bottom-1 rounded-md bg-gray-600 opacity-40"
                        style={{ width: `${(ap.ideal / 100) * 100}%` }}
                      ></div>
                      {/* Flown bar */}
                      <div
                        className="absolute top-1 bottom-1 rounded-md"
                        style={{
                          width: `${(ap.flown / 100) * 100}%`,
                          backgroundColor: ap.flown > ap.ideal + 3 ? '#F59E0B' : '#00FFD1',
                          opacity: 0.8,
                        }}
                      ></div>
                    </div>
                    <div className="flex items-center gap-1 w-16 sm:w-20 flex-shrink-0 justify-end">
                      <span className="text-xs text-gray-500">{ap.ideal}</span>
                      <span className="text-xs text-gray-600">/</span>
                      <span className={`text-xs font-bold ${ap.flown > ap.ideal + 3 ? 'text-[#F59E0B]' : 'text-[#00FFD1]'}`}>{ap.flown} kts</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-3 sm:gap-4 mt-3 sm:mt-4 text-xs text-gray-500 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-2 rounded bg-gray-600 opacity-40"></div>
                  <span>Target</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-2 rounded bg-[#00FFD1]"></div>
                  <span>Flown</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-2 rounded bg-[#F59E0B]"></div>
                  <span>High deviation</span>
                </div>
              </div>
            </div>

            {/* Feature cards */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {[
                { icon: 'ri-map-2-line', title: 'Geometry Scoring', desc: 'Leg spacing, turn timing, and altitude discipline graded per pattern.' },
                { icon: 'ri-speed-line', title: 'Speed Discipline', desc: 'Vref adherence tracked across every leg of the approach.' },
                { icon: 'ri-focus-3-line', title: 'Glidepath Tracking', desc: 'Deviation from ideal 3° glidepath measured continuously.' },
                { icon: 'ri-repeat-line', title: 'Touch &amp; Go Logging', desc: 'Each circuit logged separately for granular improvement tracking.' },
              ].map((item, i) => (
                <div key={i} className="bg-[#0F1F35] rounded-xl p-3 sm:p-5 border border-gray-700 hover:border-[#B4FF39]/40 transition-all">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center mb-2 sm:mb-3">
                    <i className={`${item.icon} text-xl sm:text-2xl text-[#B4FF39]`}></i>
                  </div>
                  <p className="text-xs sm:text-sm font-semibold text-white mb-1">{item.title}</p>
                  <p className="text-xs text-gray-500 leading-snug" dangerouslySetInnerHTML={{ __html: item.desc }}></p>
                </div>
              ))}
            </div>

            {/* Instructor note */}
            <div className="bg-[#0D1B2E] rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-700">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 sm:mb-3">Instructor Note</p>
              <p className="text-sm text-gray-300 leading-relaxed italic">
                "Your base-to-final turn is consistently 50 ft low. Try adding a touch of power earlier on base leg to maintain altitude through the turn."
              </p>
              <div className="flex items-center gap-3 mt-3 sm:mt-4">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#00A3E0]/20 flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-line text-[#00A3E0] text-sm"></i>
                </div>
                <div>
                  <p className="text-xs font-semibold text-white">CFI James Hartwell</p>
                  <p className="text-xs text-gray-500">Apr 11, 2025 · Post-flight debrief</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}